import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fornecedorService, Fornecedor, FornecedorRequest } from '../../services/fornecedor';

interface FornecedorFormProps {
  isEdit?: boolean;
  isView?: boolean;
}

const FornecedorForm: React.FC<FornecedorFormProps> = ({ isEdit = false, isView = false }) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    id: 0,
    codPessoa: 0,
    ativo: 1,
    cnpj: '',
    codEndereco: 0,
    cpf: '',
    email: '',
    nome: '',
    rg: '',
    telefone1: '',
    telefone2: '',
    bairro: '',
    cep: '',
    cidade: '',
    estado: '',
    logradouro: '',
    numero: '',
  });
  const [errors, setErrors] = useState({
    nome: '',
    telefone1: '',
    telefone2: '',
    cnpj: '',
    cep: '',
    email: '',
  });

  // Buscar fornecedor para edição
  const { data: fornecedor, isLoading } = useQuery({
    queryKey: ['fornecedor', id],
    queryFn: () => fornecedorService.getById(Number(id)),
    enabled: isEdit && !!id,
  });

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: FornecedorRequest) => fornecedorService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fornecedores'] });
      navigate('/fornecedores');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar fornecedor:', error);
    },
  });

  useEffect(() => {
    if (fornecedor && isEdit) {
      setFormData({
        id: fornecedor.id || 0,
        codPessoa: fornecedor.codPessoa || 0,
        ativo: fornecedor.ativo || 1,
        cnpj: fornecedor.cnpj || '',
        codEndereco: fornecedor.codEndereco || 0,
        cpf: fornecedor.cpf || '',
        email: fornecedor.email || '',
        nome: fornecedor.nome || '',
        rg: fornecedor.rg || '',
        telefone1: fornecedor.telefone1 || '',
        telefone2: fornecedor.telefone2 || '',
        bairro: fornecedor.bairro || '',
        cep: fornecedor.cep || '',
        cidade: fornecedor.cidade || '',
        estado: fornecedor.estado || '',
        logradouro: fornecedor.logradouro || '',
        numero: fornecedor.numero || '',
      });
    }
  }, [fornecedor, isEdit]);

  const validateForm = (): boolean => {
    const newErrors = {
      nome: '',
      telefone1: '',
      telefone2: '',
      cnpj: '',
      cep: '',
      email: '',
    };
    let isValid = true;

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
      isValid = false;
    } else if (formData.nome.length > 80) {
      newErrors.nome = 'Nome não deve possuir mais que 80 caracteres';
      isValid = false;
    } else if (formData.nome.length < 3) {
      newErrors.nome = 'Nome não deve possuir menos que 3 caracteres';
      isValid = false;
    }

    if (formData.telefone1 && formData.telefone1.length < 10) {
      newErrors.telefone1 = 'Telefone deve possuir 10 ou 11 dígitos';
      isValid = false;
    }

    if (formData.telefone2 && formData.telefone2.length < 10) {
      newErrors.telefone2 = 'Telefone deve possuir 10 ou 11 dígitos';
      isValid = false;
    }

    if (formData.cnpj && formData.cnpj.length < 18) {
      newErrors.cnpj = 'CNPJ deve possuir 18 dígitos';
      isValid = false;
    }

    if (formData.cep && formData.cep.length !== 8) {
      newErrors.cep = 'CEP deve possuir 8 dígitos';
      isValid = false;
    }

    if (formData.email && !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(formData.email)) {
      newErrors.email = 'E-mail inválido';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const fornecedorData: FornecedorRequest = {
        id: isEdit ? Number(id) : undefined,
        codPessoa: formData.codPessoa,
        ativo: formData.ativo,
        cnpj: formData.cnpj,
        codEndereco: formData.codEndereco,
        cpf: formData.cpf,
        email: formData.email,
        nome: formData.nome.trim(),
        rg: formData.rg,
        telefone1: formData.telefone1,
        telefone2: formData.telefone2,
        bairro: formData.bairro,
        cep: formData.cep,
        cidade: formData.cidade,
        estado: formData.estado,
        logradouro: formData.logradouro,
        numero: formData.numero,
      };
      
      saveMutation.mutate(fornecedorData);
    }
  };

  const handleClear = () => {
    setFormData({
      id: 0,
      codPessoa: 0,
      ativo: 1,
      cnpj: '',
      codEndereco: 0,
      cpf: '',
      email: '',
      nome: '',
      rg: '',
      telefone1: '',
      telefone2: '',
      bairro: '',
      cep: '',
      cidade: '',
      estado: '',
      logradouro: '',
      numero: '',
    });
    setErrors({
      nome: '',
      telefone1: '',
      telefone2: '',
      cnpj: '',
      cep: '',
      email: '',
    });
  };

  const handleBack = () => {
    navigate('/fornecedores');
  };

  const buscaCep = async () => {
    const cep = formData.cep.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setFormData(prev => ({
            ...prev,
            logradouro: data.logradouro,
            bairro: data.bairro,
            cidade: data.localidade,
            estado: data.uf,
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <TextField
              fullWidth
              label="Fornecedor"
              value={formData.nome}
              onChange={handleInputChange('nome')}
              error={!!errors.nome}
              helperText={errors.nome}
              slotProps={{ htmlInput: { maxLength: 80 } }}
              required
              disabled={isView}
            />
            
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Telefone 1"
                value={formData.telefone1}
                onChange={handleInputChange('telefone1')}
                error={!!errors.telefone1}
                helperText={errors.telefone1}
                slotProps={{
                htmlInput: { maxLength: 14 }
              }}
                disabled={isView}
              />
              <TextField
                fullWidth
                label="Telefone 2"
                value={formData.telefone2}
                onChange={handleInputChange('telefone2')}
                error={!!errors.telefone2}
                helperText={errors.telefone2}
                slotProps={{
                htmlInput: { maxLength: 14 }
              }}
                disabled={isView}
              />
            </Box>

            <TextField
              fullWidth
              label="E-mail"
              value={formData.email}
              onChange={handleInputChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              slotProps={{ htmlInput: { maxLength: 80 } }}
              disabled={isView}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="CNPJ"
                value={formData.cnpj}
                onChange={handleInputChange('cnpj')}
                error={!!errors.cnpj}
                helperText={errors.cnpj}
                slotProps={{
                htmlInput: { maxLength: 18 }
              }}
                disabled={isView}
                required
              />
              <TextField
                fullWidth
                label="CEP"
                value={formData.cep}
                onChange={handleInputChange('cep')}
                onBlur={buscaCep}
                error={!!errors.cep}
                helperText={errors.cep}
                slotProps={{
                htmlInput: { maxLength: 8 }
              }}
                disabled={isView}
              />
            </Box>

            <TextField
              fullWidth
              label="Rua"
              value={formData.logradouro}
              onChange={handleInputChange('logradouro')}
              slotProps={{ htmlInput: { maxLength: 80 } }}
              disabled={isView}
            />

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Número"
                value={formData.numero}
                onChange={handleInputChange('numero')}
                disabled={isView}
              />
              <TextField
                fullWidth
                label="Bairro"
                value={formData.bairro}
                onChange={handleInputChange('bairro')}
                slotProps={{ htmlInput: { maxLength: 80 } }}
                disabled={isView}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <TextField
                fullWidth
                label="Cidade"
                value={formData.cidade}
                onChange={handleInputChange('cidade')}
                disabled={isView}
              />
              <TextField
                fullWidth
                label="Estado"
                value={formData.estado}
                onChange={handleInputChange('estado')}
                disabled={isView}
              />
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 3 }}>
            {!isView && (
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveMutation.isPending}
                sx={{ minWidth: 120 }}
              >
                {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            )}
            
            {!isView && (
              <Button
                type="button"
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                sx={{ minWidth: 120 }}
              >
                Limpar
              </Button>
            )}
            
            <Button
              type="button"
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 120 }}
            >
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default FornecedorForm;
